import { MealFilter, MealSortInfo } from '@tovala/browser-apis-combinedapi'
import {
  cloneDeep,
  findIndex,
  forEach,
  intersection,
  isEmpty,
  isPlainObject,
  partition,
  some,
  sortBy,
  uniq,
} from 'lodash-es'

import {
  MenuComponentsStandardized,
  MenuComponentStandardized,
  MenuComponentStandardizedMeal,
  MenuComponentStandardizedAnimatedMealCarousel,
  MenuComponentStandardizedMealWithDecoupledAddOn,
  MenuComponentStandardizedTextImageStackChildren,
  MenuComponentStandardizedTwoMealPicker,
  convertAnimatedMealCarouselMealToBaseMeal,
  convertTwoMealPickerMealToBaseMeal,
  MenuComponentStandardizedTextBanner,
  MenuComponentStandardizedFeedback,
  MenuComponentStandardizedExtrasCarousel,
} from '@tovala/browser-apis-menu-components'
import { SelectedMealFilters } from 'types/internal'
import {
  getCookie,
  getLocalStorageItem,
  getTovalaCookieOptions,
  removeCookie,
  removeLocalStorageItem,
  setLocalStorageItem,
} from './storage'

const LONGEVITY_FILTER_KEY = 'longevity'

const MEAL_FILTERS_BY_USER_KEY = 'mealFiltersByUser'
const PENDING_RECOMMENDED_MEAL_FILTERS_KEY = 'pendingRecommendedMealFilters'

/**
 * Filters used to live in a single browser-wide slot. Read once by
 * migrateLegacyMealFilters, then dropped.
 */
const LEGACY_MEAL_FILTERS_KEY = 'mealFilters'

export interface MealGroup {
  components: MenuComponentsStandardized
  description: string | null
  id: 'filtered' | 'everythingElse'
  title: string | null
}

export function getHasSelectedFilters(
  selectedMealFilters: SelectedMealFilters,
) {
  return (
    // "selectedMealFilters" can be an empty object or it can be an object
    // like { "Dietary Preferences": boolean | string[] }.
    !isEmpty(selectedMealFilters) &&
    some(
      selectedMealFilters,
      (filterKeys) => typeof filterKeys !== 'boolean' && filterKeys.length > 0,
    )
  )
}

type MealFiltersByUser = Record<string, SelectedMealFilters>

/**
 * Local storage is hand-editable, so a stored entry is only trusted when every
 * value matches what the menu writes: false, or a list of filter keys.
 */
function parseStoredMealFilters(value: unknown): SelectedMealFilters | null {
  if (!isPlainObject(value)) {
    return null
  }

  const storedMealFilters = value as Record<string, unknown>

  const isValidShape = Object.values(storedMealFilters).every(
    (filterKeys) =>
      filterKeys === false ||
      (Array.isArray(filterKeys) &&
        filterKeys.every((filterKey) => typeof filterKey === 'string')),
  )

  return isValidShape ? (storedMealFilters as SelectedMealFilters) : null
}

/**
 * A corrupt entry is discarded on its own rather than taking the whole map with
 * it, so one account's bad data can't cost another account its filters.
 */
function getMealFiltersByUser(): MealFiltersByUser {
  const stored = getLocalStorageItem<unknown>(MEAL_FILTERS_BY_USER_KEY)

  if (!isPlainObject(stored)) {
    return {}
  }

  const mealFiltersByUser: MealFiltersByUser = {}

  forEach(stored as Record<string, unknown>, (value, userID) => {
    const selectedMealFilters = parseStoredMealFilters(value)

    if (selectedMealFilters) {
      mealFiltersByUser[userID] = selectedMealFilters
    }
  })

  return mealFiltersByUser
}

function saveMealFiltersByUser(mealFiltersByUser: MealFiltersByUser) {
  if (isEmpty(mealFiltersByUser)) {
    removeLocalStorageItem(MEAL_FILTERS_BY_USER_KEY)

    return
  }

  setLocalStorageItem<MealFiltersByUser>(
    MEAL_FILTERS_BY_USER_KEY,
    mealFiltersByUser,
  )
}

export function getSelectedMealFilters({
  userID,
}: {
  userID: number
}): SelectedMealFilters {
  return getMealFiltersByUser()[userID] ?? {}
}

/**
 * Filters live in browser-wide local storage, so they're keyed by the user who
 * picked them: a second account on the same browser keeps its own filters
 * rather than inheriting or overwriting the first account's.
 */
export function saveSelectedMealFilters(
  selectedMealFilters: SelectedMealFilters,
  userID: number,
) {
  const mealFiltersByUser = getMealFiltersByUser()

  if (isEmpty(selectedMealFilters)) {
    delete mealFiltersByUser[userID]
  } else {
    mealFiltersByUser[userID] = selectedMealFilters
  }

  saveMealFiltersByUser(mealFiltersByUser)
}

export function getHasSavedMealFiltersForUser({
  userID,
}: {
  userID: number
}): boolean {
  return getHasSelectedFilters(getSelectedMealFilters({ userID }))
}

/**
 * Moves filters saved before they were keyed by user into the current user's
 * slot, matching how the unkeyed storage behaved: it applied them to whoever
 * loaded the menu. Without this every existing filter user would silently lose
 * their filters on their first load after deploy.
 */
export function migrateLegacyMealFilters({ userID }: { userID: number }) {
  const legacyMealFilters = parseStoredMealFilters(
    getLocalStorageItem<unknown>(LEGACY_MEAL_FILTERS_KEY),
  )

  if (legacyMealFilters && !isEmpty(legacyMealFilters)) {
    const mealFiltersByUser = getMealFiltersByUser()

    // A slot written since the migration is the newer of the two, so it wins.
    if (!mealFiltersByUser[userID]) {
      mealFiltersByUser[userID] = legacyMealFilters

      saveMealFiltersByUser(mealFiltersByUser)
    }
  }

  removeLocalStorageItem(LEGACY_MEAL_FILTERS_KEY)
}

interface CheckoutHandoff {
  userID: number
}

function parseCheckoutHandoff(handoff: string): CheckoutHandoff | null {
  try {
    const parsed: unknown = JSON.parse(handoff)

    if (
      isPlainObject(parsed) &&
      typeof (parsed as Record<string, unknown>).userID === 'number'
    ) {
      return parsed as CheckoutHandoff
    }

    return null
  } catch {
    return null
  }
}

/**
 * Checkout registers the account itself and hands the customer to the menu with
 * an already-set JWT cookie, so there is no login event to hook here. It drops
 * this cookie alongside the JWT to mark that arrival. Consuming it means
 * recommended filters can only ever be applied once, never on a later relaunch.
 */
export function consumeCheckoutHandoff({
  userID,
}: {
  userID: number
}): boolean {
  const handoff = getCookie('CHECKOUT_HANDOFF')

  if (!handoff) {
    return false
  }

  // A handoff for another account is still spent, so it can't fire later.
  removeCookie('CHECKOUT_HANDOFF', getTovalaCookieOptions())

  return parseCheckoutHandoff(handoff)?.userID === userID
}

export function setPendingRecommendedMealFilters() {
  setLocalStorageItem<boolean>(PENDING_RECOMMENDED_MEAL_FILTERS_KEY, true)
}

export function clearPendingRecommendedMealFilters() {
  removeLocalStorageItem(PENDING_RECOMMENDED_MEAL_FILTERS_KEY)
}

export function consumePendingRecommendedMealFilters(): boolean {
  const hasPendingRecommendedMealFilters =
    getLocalStorageItem<boolean>(PENDING_RECOMMENDED_MEAL_FILTERS_KEY) ?? false

  clearPendingRecommendedMealFilters()

  return hasPendingRecommendedMealFilters
}

/**
 * The API returns a flat list of filter keys while the menu keys selections by
 * the filter group they belong to. Keys the API no longer offers are dropped,
 * matching how stored filters are validated.
 */
export function getRecommendedSelectedMealFilters({
  mealFilters,
  recommendedFilters,
}: {
  mealFilters: MealFilter[]
  recommendedFilters: string[] | undefined
}): SelectedMealFilters {
  if (!recommendedFilters || recommendedFilters.length === 0) {
    return {}
  }

  const recommendedSelectedMealFilters: SelectedMealFilters = {}

  mealFilters.forEach((mealFilter) => {
    const filterKeys = uniq(
      mealFilter.filters
        .flatMap((filter) => filter.options.map((option) => option.key))
        .filter((filterKey) => recommendedFilters.includes(filterKey)),
    )

    if (filterKeys.length > 0) {
      recommendedSelectedMealFilters[mealFilter.displayString] = filterKeys
    }
  })

  return recommendedSelectedMealFilters
}

export function getSelectedMealFiltersCount(
  selectedMealFilters: SelectedMealFilters,
) {
  return Object.values(selectedMealFilters).flat().filter(Boolean).length
}

/**
 * Returns the filter key when exactly one filter is selected, so callers can
 * branch on a single filter owning the menu without re-deriving that rule.
 *
 * The Blueprint experience leans on this via getShowBlueprintFirstOrderMenu,
 * which makes "menuAttributes only ever recommends one filter" a contract with
 * the backend: a second recommended key would auto-apply two filters and
 * silently drop both the Longevity theme and the collapsed menu. Only
 * "longevity" is emitted today.
 */
export function getSoleSelectedFilterKey(
  selectedMealFilters: SelectedMealFilters,
): string | null {
  if (getSelectedMealFiltersCount(selectedMealFilters) !== 1) {
    return null
  }

  const [filterKey] = Object.values(selectedMealFilters).flat().filter(Boolean)

  return typeof filterKey === 'string' ? filterKey : null
}

/*
 * The API types userAcquisitionType as a plain string, so these values are the
 * contract with the backend rather than something the compiler can check.
 */
const BLUEPRINT_ACQUISITION_TYPES: string[] = [
  'BP-Longevity',
  'TovalaBP-Longevity',
  'Longevity',
]

/**
 * An undefined acquisition type means the menu attributes query is still loading
 * or errored, which resolves to "not attributed" so those users keep the normal
 * full menu.
 */
export function getIsBlueprintAttributedUser({
  userAcquisitionType,
}: {
  userAcquisitionType: string | null | undefined
}): boolean {
  return (
    !!userAcquisitionType &&
    BLUEPRINT_ACQUISITION_TYPES.includes(userAcquisitionType)
  )
}

/**
 * Blueprint users buying into a strictly Longevity product shouldn't land on a
 * menu full of non-Blueprint meals before their first order. The experience is
 * scoped to the Longevity filter owning the menu on its own: any additional
 * filter means the user is browsing more widely and gets the normal menu back.
 */
export function getShowBlueprintFirstOrderMenu({
  isBlueprintAttributedUser,
  selectedMealFilters,
  totalOrderCount,
}: {
  isBlueprintAttributedUser: boolean
  selectedMealFilters: SelectedMealFilters
  totalOrderCount: number | undefined
}): boolean {
  return (
    isBlueprintAttributedUser &&
    // Order history is undefined while loading, so wait for a real count rather
    // than flashing the hidden menu at a customer who has ordered before.
    totalOrderCount !== undefined &&
    totalOrderCount < 1 &&
    getSoleSelectedFilterKey(selectedMealFilters) === LONGEVITY_FILTER_KEY
  )
}

/**
 * An unset userID is a view with no filtering of its own, like the public menu.
 * Those report no filters enabled rather than filters the user saved elsewhere
 * and that aren't applied to what they're looking at.
 */
export function getSelectedMealFiltersMetadata({
  userID,
}: {
  userID: number | undefined
}) {
  const selectedMealFilters = userID ? getSelectedMealFilters({ userID }) : {}
  const selectedFilters = Object.values(selectedMealFilters).flat()

  return {
    enabled_filters: selectedFilters.join(', '),
    enabled_filters_count: getSelectedMealFiltersCount(selectedMealFilters),
  }
}

export function getFilteredComponentsCounts({
  components,
}: {
  components: MenuComponentsStandardized
}) {
  let numFilteredResults = 0

  components.forEach((component) => {
    if (
      component.type !== 'meal' &&
      component.type !== 'mealWithDecoupledAddOn' &&
      component.type !== 'twoMealPicker' &&
      component.type !== 'animatedMealCarousel' &&
      component.type !== 'textImageStack'
    ) {
      return
    }

    if (component.type === 'textImageStack') {
      component.properties.children.forEach(() => {
        numFilteredResults = numFilteredResults + 1
      })
    } else {
      numFilteredResults = numFilteredResults + 1
    }
  })

  return numFilteredResults
}

export function getFilteredAllergenMealSelections({
  mealsInFilters,
  selectedMealFilters,
  selectedMealIDs,
}: {
  mealsInFilters: { key: string; mealsInFilter: number[] }[]
  selectedMealFilters: SelectedMealFilters
  selectedMealIDs: number[]
}) {
  const selectedKeys = Object.values(selectedMealFilters).flat()
  const selectedAllergenKeys = selectedKeys.filter(
    (key) => typeof key === 'string' && key.includes('allergen'),
  )

  const mealIDsInSelectedAllergenFilters = new Set(
    mealsInFilters
      .filter((mealsInFilter) =>
        selectedAllergenKeys.includes(mealsInFilter.key),
      )
      .map((mealsInFilter) => mealsInFilter.mealsInFilter)
      .flat(),
  )

  return selectedMealIDs.filter((mealID) =>
    mealIDsInSelectedAllergenFilters.has(mealID),
  )
}

function convertMealWithDecoupledAddOnToBaseMeal(
  component: MenuComponentStandardizedMealWithDecoupledAddOn,
): MenuComponentStandardizedMeal {
  return {
    id: component.id,
    properties: component.properties.meal,
    type: 'meal',
  }
}

export function convertToBaseMeal(
  component:
    | MenuComponentStandardizedMeal
    | MenuComponentStandardizedMealWithDecoupledAddOn,
): MenuComponentStandardizedMeal {
  return component.type === 'meal'
    ? component
    : convertMealWithDecoupledAddOnToBaseMeal(component)
}

export function getFilteredMenuComponents({
  allMealIDs,
  listingsInFilters,
  mealFilters,
  mealsInFilters,
  menuComponents,
  selectedMealFilters,
}: {
  allMealIDs: number[]
  listingsInFilters?: { key: string; listingsInFilters: string[] }[]
  mealFilters: MealFilter[]
  mealsInFilters: { key: string; mealsInFilter: number[] }[]
  menuComponents: MenuComponentsStandardized
  selectedMealFilters: SelectedMealFilters
}) {
  const selectedKeys = Object.values(selectedMealFilters).flat()
  const mealIDsInFilters = getMealIDsInFilters(
    selectedMealFilters,
    mealFilters,
    mealsInFilters,
    allMealIDs,
  )

  // Coupled add-ons are only checked against allergen filters; inclusion/dietary
  // filters never apply to them
  const selectedAllergenKeys = selectedKeys.filter(
    (key) => typeof key === 'string' && key.includes('allergen'),
  )
  const listingIDsInAllergenFilters = new Set(
    (listingsInFilters ?? [])
      .filter((filter) => selectedAllergenKeys.includes(filter.key))
      .flatMap((filter) => filter.listingsInFilters),
  )

  function filterMealComponent(component: MenuComponentStandardizedMeal) {
    const filtered: MenuComponentStandardizedMeal[] = []
    const remaining: MenuComponentStandardizedMeal[] = []

    if (mealIDsInFilters.includes(component.properties.id)) {
      filtered.push(component)
    } else {
      remaining.push(component)
    }

    return {
      filtered,
      remaining,
    }
  }

  function filterMealWithDecoupledAddOnComponent(
    component: MenuComponentStandardizedMealWithDecoupledAddOn,
  ) {
    const filtered: (
      | MenuComponentStandardizedMealWithDecoupledAddOn
      | MenuComponentStandardizedMeal
    )[] = []
    const remaining: (
      | MenuComponentStandardizedMealWithDecoupledAddOn
      | MenuComponentStandardizedMeal
    )[] = []

    const mealInFilter = mealIDsInFilters.includes(component.properties.meal.id)
    const addOnInFilter = !listingIDsInAllergenFilters.has(
      component.properties.addOn.id,
    )

    const resolved = addOnInFilter
      ? component
      : convertMealWithDecoupledAddOnToBaseMeal(component)

    if (mealInFilter) {
      filtered.push(resolved)
    } else {
      remaining.push(resolved)
    }

    return {
      filtered,
      remaining,
    }
  }

  function filterTwoMealPickerComponent(
    component: MenuComponentStandardizedTwoMealPicker,
  ) {
    const filtered: (
      | MenuComponentStandardizedMeal
      | MenuComponentStandardizedTwoMealPicker
    )[] = []
    const remaining: (
      | MenuComponentStandardizedMeal
      | MenuComponentStandardizedTwoMealPicker
    )[] = []

    const [mealsInFilter, mealsOutsideFilters] = partition(
      component.properties.meals,
      (meal) => mealIDsInFilters.includes(meal.id),
    )

    if (mealsInFilter.length === component.properties.meals.length) {
      filtered.push(component)
    } else if (
      mealsOutsideFilters.length === component.properties.meals.length
    ) {
      remaining.push(component)
    } else {
      mealsInFilter.forEach((meal) => {
        filtered.push(convertTwoMealPickerMealToBaseMeal(meal))
      })

      mealsOutsideFilters.forEach((meal) => {
        remaining.push(convertTwoMealPickerMealToBaseMeal(meal))
      })
    }

    return {
      filtered,
      remaining,
    }
  }

  function filterAnimatedMealCarouselComponent(
    component: MenuComponentStandardizedAnimatedMealCarousel,
  ) {
    const filtered: (
      | MenuComponentStandardizedMeal
      | MenuComponentStandardizedAnimatedMealCarousel
    )[] = []
    const remaining: (
      | MenuComponentStandardizedMeal
      | MenuComponentStandardizedAnimatedMealCarousel
    )[] = []

    const [mealsInFilter, mealsOutsideFilters] = partition(
      component.properties.mealOptions,
      (meal) => mealIDsInFilters.includes(meal.id),
    )

    if (mealsInFilter.length === component.properties.mealOptions.length) {
      filtered.push(component)
    } else if (
      mealsOutsideFilters.length === component.properties.mealOptions.length
    ) {
      remaining.push(component)
    } else {
      if (mealsInFilter.length > 1) {
        const newCarousel = cloneDeep(component)
        newCarousel.properties.mealOptions = mealsInFilter

        filtered.push(newCarousel)
      } else {
        filtered.push(
          convertAnimatedMealCarouselMealToBaseMeal(mealsInFilter[0]),
        )
      }

      if (mealsOutsideFilters.length > 1) {
        const newCarousel = cloneDeep(component)
        newCarousel.properties.mealOptions = mealsOutsideFilters

        remaining.push(newCarousel)
      } else {
        remaining.push(
          convertAnimatedMealCarouselMealToBaseMeal(mealsOutsideFilters[0]),
        )
      }
    }

    return {
      filtered,
      remaining,
    }
  }

  if (
    mealFilters.length > 0 &&
    selectedKeys.length > 0 &&
    mealIDsInFilters.length > 0
  ) {
    const filteredComponents: MenuComponentsStandardized = []
    const everythingElse: MenuComponentsStandardized = []

    const componentsToShow = menuComponents.filter((component) => {
      return (
        component.type !== 'mealCarousel' &&
        component.type !== 'orderAgainCarousel'
      )
    })

    componentsToShow.forEach((component) => {
      if (
        component.type !== 'meal' &&
        component.type !== 'mealWithDecoupledAddOn' &&
        component.type !== 'twoMealPicker' &&
        component.type !== 'animatedMealCarousel' &&
        component.type !== 'textImageStack' &&
        component.type !== 'menuFeedback' &&
        component.type !== 'extrasCarousel'
      ) {
        filteredComponents.push(component)
        return
      }

      if (component.type === 'menuFeedback') {
        everythingElse.push(component)
      }

      if (component.type === 'meal') {
        const { filtered, remaining } = filterMealComponent(component)

        filteredComponents.push(...filtered)
        everythingElse.push(...remaining)
      } else if (component.type === 'mealWithDecoupledAddOn') {
        const { filtered, remaining } =
          filterMealWithDecoupledAddOnComponent(component)

        filteredComponents.push(...filtered)
        everythingElse.push(...remaining)
      } else if (component.type === 'twoMealPicker') {
        const { filtered, remaining } = filterTwoMealPickerComponent(component)

        filteredComponents.push(...filtered)
        everythingElse.push(...remaining)
      } else if (component.type === 'animatedMealCarousel') {
        const { filtered, remaining } =
          filterAnimatedMealCarouselComponent(component)

        filteredComponents.push(...filtered)
        everythingElse.push(...remaining)

        return component.properties.mealOptions.some((option) =>
          mealIDsInFilters.includes(option.id),
        )
      } else if (component.type === 'textImageStack') {
        const childrenFilteredComponents: MenuComponentStandardizedTextImageStackChildren =
          []
        const childrenEverythingElse: MenuComponentStandardizedTextImageStackChildren =
          []

        component.properties.children.forEach((child) => {
          if (child.type === 'meal') {
            const { filtered, remaining } = filterMealComponent(child)

            childrenFilteredComponents.push(...filtered)
            childrenEverythingElse.push(...remaining)
          } else if (child.type === 'twoMealPicker') {
            const { filtered, remaining } = filterTwoMealPickerComponent(child)

            childrenFilteredComponents.push(...filtered)
            childrenEverythingElse.push(...remaining)
          } else if (child.type === 'animatedMealCarousel') {
            const { filtered, remaining } =
              filterAnimatedMealCarouselComponent(child)

            childrenFilteredComponents.push(...filtered)
            childrenEverythingElse.push(...remaining)

            return child.properties.mealOptions.some((option) =>
              mealIDsInFilters.includes(option.id),
            )
          }
        })

        if (childrenFilteredComponents.length) {
          const newTextImageStack = cloneDeep(component)
          newTextImageStack.properties.children = childrenFilteredComponents

          filteredComponents.push(newTextImageStack)
        }
        if (childrenEverythingElse.length) {
          const newTextImageStack = cloneDeep(component)
          newTextImageStack.properties.children = childrenEverythingElse

          everythingElse.push(newTextImageStack)
        }
      } else if (component.type === 'extrasCarousel') {
        everythingElse.push(component)
      }
    })

    const extrasCarousel = everythingElse.filter(
      (component) => component.type === 'extrasCarousel',
    )
    const menuFeedback = everythingElse.filter(
      (component) => component.type === 'menuFeedback',
    )
    const remainingEverythingElse = everythingElse.filter(
      (component) =>
        component.type !== 'menuFeedback' &&
        component.type !== 'extrasCarousel',
    )

    return {
      // We want the extras carousel and menu feedback to always show last in "Everything Else"
      everythingElse: [
        ...remainingEverythingElse,
        ...extrasCarousel,
        ...menuFeedback,
      ],
      filteredComponents,
      mealIDsInFilters,
    }
  }

  return {
    everythingElse: menuComponents,
    filteredComponents: [],
    mealIDsInFilters,
  }
}

function getFilteredMealGroupTitle({
  mealFilters,
  selectedMealFilters,
}: {
  mealFilters: MealFilter[]
  selectedMealFilters: SelectedMealFilters
}) {
  const selectedKeys = Object.values(selectedMealFilters)
    .flat()
    .filter((key): key is string => typeof key === 'string')

  if (selectedKeys.length !== 1) {
    return 'My Filtered Meals'
  }

  // Only inclusion filters describe the meals in the group. Exclusion filters
  // (resultsIncluded false, such as allergens) remove meals instead, so naming
  // one in the heading would misdescribe the group.
  const inclusionOption = mealFilters
    .filter((mealFilter) => mealFilter.resultsIncluded)
    .flatMap((mealFilter) => mealFilter.filters)
    .flatMap((filter) => filter.options)
    .find((option) => option.key === selectedKeys[0])

  return inclusionOption
    ? `${inclusionOption.displayString} Meals`
    : 'My Filtered Meals'
}

export function getMealGroups({
  filtersData,
  menuComponents,
  sortData,
}: {
  filtersData: {
    allMealIDs: number[]
    listingsInFilters?: { key: string; listingsInFilters: string[] }[]
    mealFilters: MealFilter[]
    mealsInFilters: { key: string; mealsInFilter: number[] }[]
    selectedMealFilters: SelectedMealFilters
  } | null
  menuComponents: MenuComponentsStandardized
  sortData: {
    menuSortOptions: { displayTitle: string; key: string }[]
    selectedMealSort: string
    sortedMealIDs: { key: string; sortedMealIDs: number[] }[]
  } | null
}) {
  let mealIDsInFilters: number[] = []
  let mealGroups: MealGroup[] = [
    {
      id: 'everythingElse',
      components: menuComponents,
      description: null,
      title: null,
    },
  ]

  if (filtersData) {
    const {
      allMealIDs,
      listingsInFilters,
      mealsInFilters,
      mealFilters,
      selectedMealFilters,
    } = filtersData
    const {
      everythingElse,
      filteredComponents,
      mealIDsInFilters: mealIDsInFiltersData,
    } = getFilteredMenuComponents({
      allMealIDs,
      listingsInFilters,
      selectedMealFilters,
      mealFilters,
      mealsInFilters,
      menuComponents,
    })

    mealIDsInFilters = mealIDsInFiltersData

    mealGroups = [
      {
        id: 'filtered',
        components: filteredComponents,
        description:
          filteredComponents.length === 0
            ? 'No meals match your filter criteria'
            : null,
        title: getFilteredMealGroupTitle({ mealFilters, selectedMealFilters }),
      },
      {
        id: 'everythingElse',
        components: everythingElse,
        description: null,
        title: 'Everything Else',
      },
    ]
  }

  // Featured is the default sort order set in Glaze by the menu team
  if (sortData && sortData.selectedMealSort !== 'featured') {
    mealGroups = mealGroups.map((mealGroup) => {
      return {
        ...mealGroup,
        components: getSortedMenuComponents({
          menuComponents: mealGroup.components,
          sortData,
        }),
      }
    })
  }

  return {
    mealGroups,
    mealIDsInFilters,
  }
}

/**
 * Removes the meals from "Everything Else" while keeping menu feedback, which
 * "getFilteredMenuComponents" already forces to the end of that group. Callers
 * render a show-all CTA in place of the hidden meals.
 */
export function collapseEverythingElseMeals({
  mealGroups,
}: {
  mealGroups: MealGroup[]
}): { hiddenMealsCount: number; mealGroups: MealGroup[] } {
  let hiddenMealsCount = 0

  const collapsedMealGroups = mealGroups.map((mealGroup) => {
    if (mealGroup.id !== 'everythingElse') {
      return mealGroup
    }

    const [keptComponents, hiddenComponents] = partition(
      mealGroup.components,
      (component) => component.type === 'menuFeedback',
    )

    hiddenMealsCount = getFilteredComponentsCounts({
      components: hiddenComponents,
    })

    return {
      ...mealGroup,
      components: keptComponents,
      description: null,
      title: null,
    }
  })

  return { hiddenMealsCount, mealGroups: collapsedMealGroups }
}

function getMealIDsInComponent(component: MenuComponentStandardized): number[] {
  switch (component.type) {
    case 'meal':
      return [component.properties.id]
    case 'mealWithDecoupledAddOn':
      return [component.properties.meal.id]
    case 'twoMealPicker':
      return component.properties.meals.map((meal) => meal.id)
    case 'animatedMealCarousel':
      return component.properties.mealOptions.map((mealOption) => mealOption.id)
    case 'mealCarousel':
      return component.properties.children.flatMap((child) =>
        getMealIDsInComponent(child),
      )
    case 'textImageStack':
      return component.properties.children.flatMap((child) =>
        getMealIDsInComponent(child),
      )
    default:
      return []
  }
}

// Returns the position of the menu component containing the given meal in the
// order the user actually sees it (filter group + sort order applied). Index is
// global across all mealGroups: filtered group first, then everythingElse.
// Returns undefined when the meal isn't visible in the current view.
export function getMealIndex({
  mealGroups,
  mealID,
}: {
  mealGroups: MealGroup[]
  mealID: number
}): number | undefined {
  let index = 0
  for (const group of mealGroups) {
    for (const component of group.components) {
      if (getMealIDsInComponent(component).includes(mealID)) {
        return index
      }
      index++
    }
  }
  return undefined
}

export function getMealIDsInFilters<FilterKey extends string>(
  selectedMealFilters: SelectedMealFilters<FilterKey>,
  allMealFilters: MealFilter[],
  mealsInFilters: { key: FilterKey; mealsInFilter: number[] }[],
  allMealIDs: number[],
) {
  const inclusionFilterKeysV2: { any: FilterKey[]; all: FilterKey[] } = {
    any: [],
    all: [],
  }
  let exclusionFilterKeys: FilterKey[] = []

  // Our filters are configured in groupings to either include or exclude meals.
  // For example, the "Dietary Preferences" filter group is set up to include
  // meals, so if a user selects the "low_sodium" filter, we'll only return meals
  // that have low sodium. On the other hand, the "Allergens" filter group is set
  // up to exclude meals, so if a user selects the "allergen_eggs" filter, we'll
  // exclude meals that have an eggs allergen (i.e. the user is looking for meals
  // they won't have an allergic reaction to).
  for (const [key, value] of Object.entries(selectedMealFilters)) {
    if (typeof value === 'boolean') {
      continue
    }

    const filterGroup = allMealFilters.find(
      (filter) => filter.displayString === key,
    )

    if (filterGroup && filterGroup.resultsIncluded) {
      if (filterGroup.resultsMatched === 'all') {
        inclusionFilterKeysV2.all = [...inclusionFilterKeysV2.all, ...value]
      } else {
        inclusionFilterKeysV2.any = [...inclusionFilterKeysV2.any, ...value]
      }
    } else {
      exclusionFilterKeys = [...exclusionFilterKeys, ...value]
    }
  }

  let includeAllMealIDs = [...allMealIDs]
  let includeAnyMealIDs = [...allMealIDs]

  if (inclusionFilterKeysV2.all.length) {
    // If some include 'all' filters are selected, get the meals that are in ALL filters (as opposed to ANY).
    const includeMealsInFilters = getMealsInFilters(
      inclusionFilterKeysV2.all,
      mealsInFilters,
    )

    includeAllMealIDs = intersection(...includeMealsInFilters)
  }

  if (inclusionFilterKeysV2.any.length) {
    const includeAnyMealsInFilters = getMealsInFilters(
      inclusionFilterKeysV2.any,
      mealsInFilters,
    )
    includeAnyMealIDs = uniq(includeAnyMealsInFilters.flat())
  }

  const excludeMealsInFilters = getMealsInFilters(
    exclusionFilterKeys,
    mealsInFilters,
  )
  const excludeMealIDs = new Set(excludeMealsInFilters.flat())

  return intersection(includeAllMealIDs, includeAnyMealIDs).filter(
    (mealID) => !excludeMealIDs.has(mealID),
  )
}

function getMealsInFilters<FilterKey extends string>(
  filters: FilterKey[],
  menuFilters: { key: string; mealsInFilter: number[] }[],
) {
  return filters.map((key) => {
    const selectedFilter = menuFilters.find((filter) => filter.key === key)

    return selectedFilter?.mealsInFilter ?? []
  })
}

export function getMealSortOptions({
  mealSortInfo,
}: {
  mealSortInfo: MealSortInfo | undefined
}) {
  return mealSortInfo
    ? [
        { displayTitle: 'Featured', key: 'featured' },
        ...mealSortInfo.sortKeys.map(({ displayTitle, key, defaultOrder }) => {
          return {
            displayTitle: `${displayTitle} (${defaultOrder === 'asc' ? 'low to high' : 'high to low'})`,
            key,
          }
        }),
      ]
    : []
}

/*
  Returns an array of meal IDs sorted by the selected sort key. The mealAttribute values return will always be strings,
  but the sortType will tell us if we need to convert the values to numbers. We currently only use the default sort order,
  it can't be reversed in the UI.
*/
export function getSortedMealIDs({
  mealSortInfo,
}: {
  mealSortInfo: MealSortInfo | undefined
}) {
  return (
    mealSortInfo?.sortKeys.map(({ defaultOrder, key, sortType }) => {
      return {
        key,
        sortedMealIDs: [...mealSortInfo.mealAttributes]
          .sort((a, b) => {
            if (key in a && key in b) {
              if (
                sortType === 'string' &&
                typeof a[key] === 'string' &&
                typeof b[key] === 'string'
              ) {
                if (defaultOrder === 'asc') {
                  return a[key].localeCompare(b[key])
                }
                return b[key].localeCompare(a[key])
              } else if (
                sortType === 'number' &&
                typeof a[key] === 'string' &&
                typeof b[key] === 'string'
              ) {
                if (defaultOrder === 'asc') {
                  return Number(a[key]) - Number(b[key])
                }
                return Number(b[key]) - Number(a[key])
              }

              return 0
            }

            return 0
          })
          .map((mealAttribute) => {
            return mealAttribute.mealID
          }),
      }
    }) ?? []
  )
}

function getSortedMenuComponents({
  menuComponents,
  sortData,
}: {
  menuComponents: MenuComponentsStandardized
  sortData: {
    menuSortOptions: { displayTitle: string; key: string }[]
    selectedMealSort: string
    sortedMealIDs: { key: string; sortedMealIDs: number[] }[]
  }
}) {
  const sortedMealIDs =
    sortData.sortedMealIDs.find(
      (sortedMealIDs) => sortedMealIDs.key === sortData.selectedMealSort,
    )?.sortedMealIDs ?? []

  if (sortedMealIDs) {
    const [componentsToSort, otherComponents] = partition(
      menuComponents,
      (component) => {
        return (
          component.type === 'meal' ||
          component.type === 'animatedMealCarousel' ||
          component.type === 'mealWithDecoupledAddOn' ||
          component.type === 'textImageStack'
        )
      },
    )

    const sortableMenuComponents: (
      | MenuComponentStandardizedMeal
      | MenuComponentStandardizedMealWithDecoupledAddOn
    )[] = []

    componentsToSort.forEach((component) => {
      if (component.type === 'animatedMealCarousel') {
        component.properties.mealOptions.forEach((mealOption) => {
          sortableMenuComponents.push(
            convertAnimatedMealCarouselMealToBaseMeal(mealOption),
          )
        })
      } else if (component.type === 'textImageStack') {
        component.properties.children.forEach((child) => {
          if (child.type === 'animatedMealCarousel') {
            child.properties.mealOptions.forEach((mealOption) => {
              sortableMenuComponents.push(
                convertAnimatedMealCarouselMealToBaseMeal(mealOption),
              )
            })
          } else if (child.type === 'meal') {
            sortableMenuComponents.push(child)
          }
        })
      } else if (
        component.type === 'meal' ||
        component.type === 'mealWithDecoupledAddOn'
      ) {
        sortableMenuComponents.push(component)
      }
    })

    const sortedComponents = sortBy(sortableMenuComponents, (component) => {
      return findIndex(sortedMealIDs, (mealID) => {
        if (component.type === 'meal') {
          return component.properties.id === mealID
        } else if (component.type === 'mealWithDecoupledAddOn') {
          return component.properties.meal.id === mealID
        }

        return false
      })
    })

    const extrasCarouselComponents: MenuComponentStandardizedExtrasCarousel[] =
      otherComponents.filter((component) => component.type === 'extrasCarousel')
    const textBannerComponents: MenuComponentStandardizedTextBanner[] =
      otherComponents.filter((component) => component.type === 'textBanner')
    const menuFeedbackComponents: MenuComponentStandardizedFeedback[] =
      otherComponents.filter((component) => component.type === 'menuFeedback')

    return [
      ...textBannerComponents,
      ...sortedComponents,
      ...extrasCarouselComponents,
      ...menuFeedbackComponents,
    ]
  }

  // If no matching sort order is found, return the menu components as is
  return menuComponents
}

/*
  Returns a subset of the selected meal filters that are valid for the current available meal filters.
  This is used to ensure that the selected meal filters stored in local storage are not invalid due to
  changes in the meal filters.
*/
export function getValidSelectedMealFilters({
  mealFilters,
  selectedMealFilters,
}: {
  mealFilters: MealFilter[]
  selectedMealFilters: SelectedMealFilters
}) {
  const categories = mealFilters.map((mealFilter) => ({
    category: mealFilter.displayString,
    filterKeys: mealFilter.filters.flatMap((filter) =>
      filter.options.map((option) => option.key),
    ),
  }))

  const getIsOfferedIn = (filterKey: string, category: string) =>
    categories.some(
      (offering) =>
        offering.category === category &&
        offering.filterKeys.includes(filterKey),
    )

  const getCategoryOfferingFilterKey = (filterKey: string) =>
    categories.find(({ filterKeys }) => filterKeys.includes(filterKey))
      ?.category

  const validSelectedMealFilters: SelectedMealFilters = {}

  // In the rare case a filter has been moved to another category,
  // we want to preserve the selection in the new category
  forEach(selectedMealFilters, (savedFilterKeys, savedCategory) => {
    if (!Array.isArray(savedFilterKeys)) {
      return
    }

    savedFilterKeys.forEach((filterKey) => {
      const category = getIsOfferedIn(filterKey, savedCategory)
        ? savedCategory
        : getCategoryOfferingFilterKey(filterKey)

      if (!category) {
        return
      }

      const categoryFilterKeys = validSelectedMealFilters[category] || []

      validSelectedMealFilters[category] = uniq([
        ...categoryFilterKeys,
        filterKey,
      ])
    })
  })

  return validSelectedMealFilters
}
